import React, { useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';

// ─── Options ──────────────────────────────────────────────────────────────────

const LEAD_SOURCES = ['Digital Ad', 'Website', 'App', 'Walk-In', 'Referral', 'Lead Engine', 'Other'];
const LEVELS       = ['Beginner', 'Intermediate', 'Advanced', 'Unknown'];

const TRIAL_ASK_RESULTS = [
  'Sold Trial',
  'Sold Membership',
  'Sold Punch Card',
  'Sales Follow-Up Needed',
  'Declined — Price',
  'Declined — Timing',
  'Declined — Not Ready',
  'Declined — Prefers Drop In',
  'Declined — Already Plays Elsewhere',
  'Declined — Not Enough Time to Commit',
  'Failed to Ask',
  'No Show',
];

const SALE_TYPES = ['Trial Sold', 'Membership Sold', 'Sold Punch Card'];

// ─── Date helpers ─────────────────────────────────────────────────────────────

function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d; }
function toISO(date)       { return date.toISOString().split('T')[0]; }
function fmtDisplay(iso)   {
  if (!iso) return '';
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

const QUICK_PICKS = [
  { label: 'Today',    days: 0 },
  { label: 'Tomorrow', days: 1 },
  { label: '+3 days',  days: 3 },
  { label: '+1 week',  days: 7 },
];

const TIME_OPTIONS = [
  '8:00 AM','8:30 AM','9:00 AM','9:30 AM','10:00 AM','10:30 AM',
  '11:00 AM','11:30 AM','12:00 PM','12:30 PM','1:00 PM','1:30 PM',
  '2:00 PM','2:30 PM','3:00 PM','3:30 PM','4:00 PM','4:30 PM',
  '5:00 PM','5:30 PM','6:00 PM','6:30 PM','7:00 PM','7:30 PM','8:00 PM',
];

// ─── MiniCalendar ─────────────────────────────────────────────────────────────

function MiniCalendar({ selectedDate, minDate, onSelect }) {
  const today = new Date();
  const init  = selectedDate ? new Date(selectedDate + 'T12:00:00') : today;
  const [viewYear,  setViewYear]  = useState(init.getFullYear());
  const [viewMonth, setViewMonth] = useState(init.getMonth());

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const DAYS   = ['Su','Mo','Tu','We','Th','Fr','Sa'];

  function prev() { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y-1); } else setViewMonth(m => m-1); }
  function next() { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y+1); } else setViewMonth(m => m+1); }

  const firstDay    = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth+1, 0).getDate();
  const minISO      = minDate || toISO(today);
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div style={{ background: '#0d1117', border: '1px solid #333', borderRadius: 8, padding: 10, userSelect: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <button onClick={prev} style={{ background: 'none', border: 'none', color: '#888', fontSize: 16, cursor: 'pointer', padding: '0 6px' }}>‹</button>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#e5e5e5' }}>{MONTHS[viewMonth]} {viewYear}</span>
        <button onClick={next} style={{ background: 'none', border: 'none', color: '#888', fontSize: 16, cursor: 'pointer', padding: '0 6px' }}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
        {DAYS.map(d => <div key={d} style={{ textAlign: 'center', fontSize: 9, color: '#555', fontWeight: 700 }}>{d}</div>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const iso     = `${viewYear}-${String(viewMonth+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const isSel   = iso === selectedDate;
          const isToday = iso === toISO(today);
          const isPast  = minDate && iso < minISO;
          return (
            <button key={iso} disabled={isPast} onClick={() => !isPast && onSelect(iso)} style={{
              background: isSel ? '#facc15' : isToday ? '#facc1520' : 'transparent',
              color: isSel ? '#000' : isPast ? '#333' : isToday ? '#facc15' : '#ccc',
              border: isSel ? 'none' : isToday ? '1px solid #facc1540' : '1px solid transparent',
              borderRadius: 4, padding: '4px 2px', fontSize: 11,
              cursor: isPast ? 'not-allowed' : 'pointer', fontWeight: isSel ? 700 : 400,
            }}>{day}</button>
          );
        })}
      </div>
    </div>
  );
}

// ─── DatePicker ───────────────────────────────────────────────────────────────

function DatePicker({ label, value, onChange, minDate }) {
  const [open, setOpen] = useState(false);
  const today = new Date();
  return (
    <div className="field-group">
      {label && <label className="field-label">{label}</label>}
      <button type="button" onClick={() => setOpen(p => !p)} style={{
        width: '100%', textAlign: 'left', background: '#111',
        border: `1px solid ${value ? '#facc15' : '#333'}`,
        borderRadius: 6, padding: '8px 12px', fontSize: 13,
        color: value ? '#facc15' : '#555', cursor: 'pointer',
      }}>
        {value ? `📅 ${fmtDisplay(value)}` : '📅 Select date…'}
      </button>
      {open && (
        <div style={{ marginTop: 6 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {QUICK_PICKS.map(p => (
              <button key={p.label} type="button"
                onClick={() => { onChange(toISO(addDays(today, p.days))); setOpen(false); }}
                style={{ fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer', border: '1px solid #333', background: '#111', color: '#888' }}>
                {p.label}
              </button>
            ))}
          </div>
          <MiniCalendar selectedDate={value} minDate={minDate} onSelect={d => { onChange(d); setOpen(false); }} />
          {value && (
            <button type="button" onClick={() => { onChange(''); }}
              style={{ background: 'none', border: 'none', color: '#666', fontSize: 11, cursor: 'pointer', marginTop: 4 }}>
              ✕ Clear date
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── FollowUpScheduler ────────────────────────────────────────────────────────

function FollowUpScheduler({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const today = new Date();
  return (
    <div style={{ background: '#0d1117', border: '1px solid #22c55e33', borderRadius: 8, padding: '10px 12px', marginTop: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#22c55e', marginBottom: 8 }}>📅 Schedule Sales Follow-Up</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {QUICK_PICKS.map(p => {
          const iso = toISO(addDays(today, p.days));
          return (
            <button key={p.label} type="button"
              onClick={() => { onChange({ ...value, date: iso }); setOpen(false); }}
              style={{
                fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                border: `1px solid ${value.date === iso ? '#22c55e' : '#333'}`,
                background: value.date === iso ? '#22c55e15' : '#111',
                color: value.date === iso ? '#22c55e' : '#888',
              }}>{p.label}</button>
          );
        })}
        <button type="button" onClick={() => setOpen(p => !p)} style={{
          fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
          border: '1px solid #333', background: '#111', color: '#888',
        }}>📅 Pick date</button>
      </div>
      {open && (
        <div style={{ marginBottom: 8 }}>
          <MiniCalendar selectedDate={value.date} minDate={toISO(today)} onSelect={d => { onChange({ ...value, date: d }); setOpen(false); }} />
        </div>
      )}
      {value.date && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>✓ {fmtDisplay(value.date)}</span>
          <select value={value.time || ''} onChange={e => onChange({ ...value, time: e.target.value || null })}
            style={{ background: '#111', border: '1px solid #333', color: value.time ? '#22c55e' : '#666', borderRadius: 5, padding: '4px 8px', fontSize: 11 }}>
            <option value=''>Any time</option>
            {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <button type="button" onClick={() => onChange({ date: null, time: null })}
            style={{ background: 'none', border: 'none', color: '#666', fontSize: 11, cursor: 'pointer' }}>✕ clear</button>
        </div>
      )}
    </div>
  );
}

// ─── SectionLabel + helpers ───────────────────────────────────────────────────

function SectionLabel({ children, color }) {
  return (
    <div style={{
      fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)',
      textTransform: 'uppercase', letterSpacing: '0.06em',
      marginBottom: '0.5rem', paddingTop: '0.75rem',
      borderTop: '1px solid var(--border)',
      borderLeftColor: color || 'var(--border)', borderLeftWidth: 3,
      borderLeftStyle: 'solid', paddingLeft: '0.5rem',
    }}>{children}</div>
  );
}

function Field({ label, children, span }) {
  return (
    <div className="field-group" style={span ? { gridColumn: 'span 2' } : {}}>
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}

function CheckField({ label, value, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', color: '#e5e5e5' }}>
      <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function PB101NewRecord({ onClose, onCreated }) {
  const { post }        = useApi();
  const { currentClub } = useClub();
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const today = new Date();

  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    leadSource: '',
    sessionDate:      toISO(today),
    openPlayInvited:  false, openPlayReserved: false,
    openPlayInvitedBy: '',
    attendedLtp:      false, stayedForPlay:    false,
    trialOfferedBy:   '', trialAskResult:   '',
    saleType: '', trialStartDate: '', notes: '',
    followUpDate: null, followUpTime: null,
  });

  function update(f, v) { setForm(x => ({ ...x, [f]: v })); }

  const isSold        = ['Sold Trial','Sold Membership','Sold Punch Card'].includes(form.trialAskResult);
  const isSalesHandoff = form.trialAskResult === 'Sales Follow-Up Needed';
  const isDeclined    = form.trialAskResult && (form.trialAskResult.startsWith('Declined') || form.trialAskResult === 'Failed to Ask' || form.trialAskResult === 'No Show');
  const sessionIsPast = form.sessionDate && form.sessionDate <= toISO(today);

  async function handleSave() {
    if (!form.firstName || !form.lastName) { setError('First and last name are required.'); return; }
    setSaving(true); setError(null);
    try {
      const result = await post('/pb101/record', {
        firstName:        form.firstName.trim(),
        lastName:         form.lastName.trim(),
        email:            form.email.trim() || null,
        phone:            form.phone.replace(/\D/g, '').slice(-10) || null,
        leadSource:       form.leadSource || null,
        sessionDate:      form.sessionDate,
        openPlayInvited:  form.openPlayInvited,
        openPlayInvitedBy: form.openPlayInvitedBy.trim() || null,
        openPlayReserved: form.openPlayReserved,
        attendedLtp:      form.attendedLtp,
        stayedForPlay:    form.stayedForPlay,
        trialOfferedBy:   form.trialOfferedBy || null,
        trialAskResult:   form.trialAskResult || null,
        saleType:         form.saleType || null,
        trialStartDate:   form.trialStartDate || null,
        notes:            form.notes || null,
        followUpDate:     form.followUpDate || null,
        followUpTime:     form.followUpTime || null,
        locationId:       currentClub.id,
      });
      onCreated(result?.recordId || null);
    } catch (e) {
      setError(e.message || 'Failed to create record.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}
        style={{ maxWidth: '580px', width: '100%', maxHeight: '92vh', overflowY: 'auto' }}>

        <div className="modal-title">New PB101 Record</div>

        {error && (
          <div style={{ padding: '0.6rem 0.75rem', background: 'rgba(255,50,50,0.1)', border: '1px solid var(--red)', borderRadius: '6px', fontSize: '0.83rem', color: 'var(--text)', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {/* ── 1. Profile ── */}
        <SectionLabel color="#3b82f6">F. DESK — Profile</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <Field label="First Name *"><input className="field-input" value={form.firstName} onChange={e => update('firstName', e.target.value)} /></Field>
          <Field label="Last Name *"><input className="field-input" value={form.lastName} onChange={e => update('lastName', e.target.value)} /></Field>
          <Field label="Phone"><input className="field-input" value={form.phone} placeholder="10-digit" onChange={e => update('phone', e.target.value)} /></Field>
          <Field label="Email"><input className="field-input" value={form.email} onChange={e => update('email', e.target.value)} /></Field>
          <Field label="Lead Source">
            <select className="field-select" value={form.leadSource} onChange={e => update('leadSource', e.target.value)}>
              <option value="">Select...</option>
              {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </div>

        {/* ── 2. Session ── */}
        <SectionLabel color="#3b82f6">F. DESK — PB101 Session</SectionLabel>
        <div style={{ marginBottom: '0.75rem' }}>
          <DatePicker label="Session Date" value={form.sessionDate} onChange={v => update('sessionDate', v)} />
        </div>

        {/* Open Play flags — always visible */}
        <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
          <CheckField label="Open Play Invited"  value={form.openPlayInvited}  onChange={v => update('openPlayInvited', v)} />
          <CheckField label="Open Play Reserved" value={form.openPlayReserved} onChange={v => update('openPlayReserved', v)} />
        </div>

        {/* Invited By — only relevant once an invite has actually happened.
            Simple free-text name, same pattern as trialOfferedBy below —
            covers club staff or Josh, whoever made the ask (e.g. Josh may
            invite someone to Open Play himself while on the phone with them,
            before they've ever set foot in the club). */}
        {form.openPlayInvited && (
          <div style={{ marginBottom: '0.75rem' }}>
            <Field label="Invited By">
              <input className="field-input" value={form.openPlayInvitedBy}
                placeholder="Name of whoever made the invite"
                onChange={e => update('openPlayInvitedBy', e.target.value)} />
            </Field>
          </div>
        )}

        {/* Attendance — only when session is today or past */}
        {sessionIsPast && (
          <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            <CheckField label="Attended PB101"  value={form.attendedLtp}   onChange={v => update('attendedLtp', v)} />
            <CheckField label="Stayed for Play" value={form.stayedForPlay} onChange={v => update('stayedForPlay', v)} />
          </div>
        )}

        {/* ── 3. Day-of Trial Ask — only when session today or past ── */}
        {sessionIsPast && (
          <>
            <SectionLabel color="#eab308">SALES OR F. DESK — Day-of Trial Ask</SectionLabel>
            <div style={{ marginBottom: '0.75rem' }}>
              <label className="field-label">What happened with the trial ask?</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 6 }}>
                {TRIAL_ASK_RESULTS.map(r => {
                  const sold     = ['Sold Trial','Sold Membership','Sold Punch Card'].includes(r);
                  const handoff  = r === 'Sales Follow-Up Needed';
                  const color    = sold ? '#22c55e' : handoff ? '#ef4444' : '#555';
                  const selected = form.trialAskResult === r;
                  return (
                    <button key={r} type="button"
                      onClick={() => update('trialAskResult', selected ? '' : r)}
                      style={{
                        textAlign: 'left', padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                        border: `1px solid ${selected ? color : '#2a2a2a'}`,
                        background: selected ? color + '18' : '#0d0d0d',
                        color: selected ? color : '#666',
                        fontSize: 11, fontWeight: selected ? 700 : 400,
                      }}>
                      {sold ? '✅ ' : handoff ? '🔥 ' : ''}{r}
                    </button>
                  );
                })}
              </div>
            </div>

            {isSalesHandoff && (
              <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 7, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#ef4444', fontWeight: 700 }}>
                🔥 HOT LEAD — Sales will be notified. Contact within 4 hours.
              </div>
            )}

            {isSold && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <Field label="Sale Type">
                  <select className="field-select" value={form.saleType} onChange={e => update('saleType', e.target.value)}>
                    <option value="">Select...</option>
                    {SALE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <DatePicker label="Trial Start Date" value={form.trialStartDate} onChange={v => update('trialStartDate', v)} />
              </div>
            )}

            <div style={{ marginBottom: '0.75rem' }}>
              <Field label="Trial Offered By">
                <input className="field-input" value={form.trialOfferedBy} placeholder="Staff name" onChange={e => update('trialOfferedBy', e.target.value)} />
              </Field>
            </div>

            {(isDeclined || isSalesHandoff) && (
              <>
                <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>
                  {isSalesHandoff ? 'Schedule when Sales should follow up:' : 'Optionally schedule a Sales follow-up:'}
                </div>
                <FollowUpScheduler
                  value={{ date: form.followUpDate, time: form.followUpTime }}
                  onChange={v => { update('followUpDate', v.date); update('followUpTime', v.time); }}
                />
              </>
            )}
          </>
        )}

        {/* ── 4. Notes ── */}
        <div style={{ marginTop: '0.75rem' }}>
          <Field label="Notes">
            <textarea className="field-textarea" rows={3}
              placeholder="Notes from the session — saved to member history across all modules..."
              value={form.notes} onChange={e => update('notes', e.target.value)} />
            <div style={{ fontSize: 10, color: '#555', marginTop: 3 }}>Visible across T2M, Lead Engine, PB101</div>
          </Field>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave}
            disabled={!form.firstName || !form.lastName || saving}
            style={isSold ? { background: '#22c55e', borderColor: '#22c55e', color: '#000' } : {}}>
            {saving ? 'Saving...' : isSold ? '✅ Create Record — Sale!' : 'Create Record'}
          </button>
        </div>
      </div>
    </div>
  );
}
